import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
    ArrowLeft,
    Check,
    Loader2,
    Phone,
    SlidersHorizontal,
    X,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { z } from 'zod';

import { APP_PAGE_GUTTER_CLASSNAME } from '@apps/web/components/layout/page_layout';
import {
    TrainingModePageTourReplayButton,
    TrainingModePageTourScope,
} from '@apps/web/components/page_tour/training_mode_page_tour';
import { VOICE_CALLS_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import {
    LocalSavedViewsBar,
    localSavedViewsStorageKey,
    useLocalSavedViews,
} from '@apps/web/components/saved_views/local_saved_views';
import { ConversationsPageTabs } from '@apps/web/components/shared/messages/conversations_page_tabs';
import {
    INBOX_PILL_COMPACT_CLASS,
    InboxFilterPill,
} from '@apps/web/components/shared/messages/inbox_filter_pills';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { DatePicker } from '@apps/web/components/ui/date_picker';
import { EmptyState } from '@apps/web/components/ui/empty_state';
import { FilterBar } from '@apps/web/components/ui/filter_bar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { SearchInput } from '@apps/web/components/ui/search_input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { CallDetailDialog } from '@apps/web/components/voice_calls/call_detail_dialog';
import { CallListRows } from '@apps/web/components/voice_calls/call_list_rows';
import { VoiceCallsDetailPane } from '@apps/web/components/voice_calls/voice_calls_detail_pane';
import {
    SearchOlderVoiceCallsButton,
    VoiceCallsEmptyState,
} from '@apps/web/components/voice_calls/voice_calls_empty_state';
import { VoiceCallsTable } from '@apps/web/components/voice_calls/voice_calls_table';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useCoalescedCallback } from '@apps/web/hooks/use_coalesced_callback';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import {
    localFilterMemoryStorageKey,
    useLocalFilterMemory,
} from '@apps/web/hooks/use_local_filter_memory';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOnModeChange } from '@apps/web/hooks/use_on_mode_change';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useResourceOrganizationLookup } from '@apps/web/hooks/use_resource_organization_lookup';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { ACTIVITY_TOPIC_FILTER_OPTIONS } from '@apps/web/lib/activity_topic_filter_options';
import {
    formatPhoneNumber,
    isS3RecordingUrl,
} from '@apps/web/lib/contact_utils';
import { formatDateKeyLong } from '@apps/web/lib/date_utils';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useMode } from '@apps/web/lib/mode_store';
import { cn } from '@apps/web/lib/utils';
import {
    buildVoiceCallFilterDimensions,
    voiceCallFilterChips,
    voiceCallFilterChipValues,
    type VoiceCallFilterKey,
} from '@apps/web/routes/_app/(main)/voice-calls-filter-chips';
import {
    type ActivityTopicFilter,
    useInfiniteVoiceCalls,
    useOrganization,
    useOrganizationCallRoutingDestinations,
    useOrganizationOffices,
    useOrganizationVoiceCall,
    useUser,
    useVoiceCallFilterParticipants,
    useVoiceCallRecordingUrls,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError } from '@libraries/typescript/api_client';
import { type OrganizationOffice } from '@libraries/typescript/api_client/schema_types';

export const CALLS_PAGE_SIZE = 15;

export function isVoiceCallsPageLoading(
    filterMemoryHydrated: boolean,
    callsLoading: boolean,
): boolean {
    return !filterMemoryHydrated || callsLoading;
}
// Distance from the bottom of the call list at which the next page loads.
const INFINITE_SCROLL_THRESHOLD_PX = 200;
const ALL_CAREGIVERS_VALUE = '__all__';
const ALL_CLIENTS_VALUE = '__all__';
const ALL_DIRECTIONS_VALUE = 'all';
const ALL_CALL_TYPES_VALUE = 'all';
const ALL_TRANSFER_STATUSES_VALUE = 'all';
const ALL_TRANSFER_DESTINATIONS_VALUE = 'all';
const ALL_LINES_VALUE = 'all';
const ALL_NOTES_VALUE = 'all';
const ALL_VOICEMAIL_STATUSES_VALUE = 'all';
const ALL_ASSIGNMENTS_VALUE = 'all';
const ALL_OFFICES_VALUE = 'all';
const ALL_TOPICS_VALUE = 'all';
const NEEDS_REVIEW_NOTES_VALUE = 'needs_review';
const SEARCH_DEBOUNCE_DELAY_MS = 300;
const VOICE_CALL_REALTIME_REFRESH_DELAY_MS = 3_000;

type VoiceCallRealtimeRefreshOptions = {
    readonly organizationId: string | null;
    readonly selectedCallId: string | null;
    readonly refreshCallsPage: () => Promise<unknown> | unknown;
    readonly refreshSelectedCallDetail: () => Promise<unknown> | unknown;
};

/**
 * Keeps voice-call list and detail data current without stacking requests.
 *
 * The event handler remains unthrottled so an event for the selected call is
 * never replaced by a later unrelated event. Each refresh is throttled and
 * single-flight independently after the resource decision is made.
 */
export function useVoiceCallRealtimeRefresh({
    organizationId,
    selectedCallId,
    refreshCallsPage,
    refreshSelectedCallDetail,
}: VoiceCallRealtimeRefreshOptions) {
    const coalescedCallsPageRefresh = useCoalescedCallback(refreshCallsPage);
    const throttledCallsPageRefresh = useThrottledCallback(
        coalescedCallsPageRefresh,
        VOICE_CALL_REALTIME_REFRESH_DELAY_MS,
    );
    const coalescedSelectedCallRefresh = useCoalescedCallback(
        refreshSelectedCallDetail,
    );
    const throttledSelectedCallRefresh = useThrottledCallback(
        coalescedSelectedCallRefresh,
        VOICE_CALL_REALTIME_REFRESH_DELAY_MS,
    );

    useListenForOrganizationEvent(
        'voice_call_updated',
        (event) => {
            if (event.resource.type !== 'voice_call') {
                return;
            }

            throttledCallsPageRefresh();
            if (event.resource.id === selectedCallId) {
                throttledSelectedCallRefresh();
            }
        },
        { organizationId },
    );
}

type VoiceCallDirection = 'inbound' | 'outbound';
type CallDirectionFilterValue = 'all' | VoiceCallDirection;
type CallTypeFilterValue = 'all' | 'client' | 'caregiver' | 'notification';
type TransferStatusFilterValue = 'all' | 'transferred' | 'not_transferred';
// The shared activity topic taxonomy minus "messages", which only maps to
// SMS escalation events and can never match a call. A call's topic is
// derived from its voice-call-note activity events server-side.
type CallTopicValue = Exclude<ActivityTopicFilter, 'messages'>;
type CallTopicFilterValue = typeof ALL_TOPICS_VALUE | CallTopicValue;
type NotesFilterValue =
    | typeof ALL_NOTES_VALUE
    | typeof NEEDS_REVIEW_NOTES_VALUE;
type VoicemailStatusFilterValue =
    | typeof ALL_VOICEMAIL_STATUSES_VALUE
    | 'voicemail'
    | 'not_voicemail';
type AssignmentFilterValue =
    | typeof ALL_ASSIGNMENTS_VALUE
    | 'assigned'
    | 'unassigned';

function isCallTopicOption(option: {
    readonly value: ActivityTopicFilter;
    readonly label: string;
}): option is { readonly value: CallTopicValue; readonly label: string } {
    return option.value !== 'messages';
}

export const CALL_TOPIC_OPTIONS =
    ACTIVITY_TOPIC_FILTER_OPTIONS.filter(isCallTopicOption);

function isCallTopicValue(value: string): value is CallTopicValue {
    return CALL_TOPIC_OPTIONS.some((option) => option.value === value);
}

export type PendingVoiceCallFilters = {
    readonly caregiverId: string;
    readonly clientId: string;
    readonly dateFrom: string | undefined;
    readonly dateTo: string | undefined;
    readonly direction: CallDirectionFilterValue;
    readonly callType: CallTypeFilterValue;
    readonly transferStatus: TransferStatusFilterValue;
    readonly transferDestinationId: string;
    readonly lineNumber: string;
    readonly voicemailStatus: VoicemailStatusFilterValue;
    readonly notesFilter: NotesFilterValue;
    readonly assignment: AssignmentFilterValue;
    readonly topic: CallTopicFilterValue;
    readonly search: string;
    readonly officeIds: ReadonlyArray<string>;
};

export type AppliedFilters = {
    readonly caregiverId: string | undefined;
    readonly clientId: string | undefined;
    readonly dateFrom: string | undefined;
    readonly dateTo: string | undefined;
    readonly direction: VoiceCallDirection | undefined;
    readonly callType: 'client' | 'caregiver' | 'notification' | undefined;
    readonly transferredToBackOffice: boolean | undefined;
    readonly transferDestinationId: string | undefined;
    readonly lineNumber: string | undefined;
    readonly voicemail: boolean | undefined;
    readonly notesNeedsReview: boolean | undefined;
    readonly assignment: 'assigned' | 'unassigned' | undefined;
    readonly topic: CallTopicValue | undefined;
    readonly search: string | undefined;
    readonly officeIds: ReadonlyArray<string> | undefined;
};

export const voiceCallSavedViewPayloadSchema = z.object({
    caregiverId: z.string().nullable(),
    clientId: z.string().nullable(),
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    direction: z.enum(['inbound', 'outbound']).nullable(),
    callType: z.enum(['client', 'caregiver', 'notification']).nullable(),
    transferredToBackOffice: z.boolean().nullable(),
    transferDestinationId: z.string().nullable(),
    // Defaulted so views saved before the line filter existed still parse.
    lineNumber: z.string().nullable().default(null),
    voicemail: z.boolean().nullable(),
    notesNeedsReview: z.boolean().nullable(),
    assignment: z.enum(['assigned', 'unassigned']).nullable(),
    topic: z.string().refine(isCallTopicValue).nullable(),
    officeIds: z.array(z.string()),
    view: z.enum(['list', 'table']),
});

export type VoiceCallSavedViewPayload = z.infer<
    typeof voiceCallSavedViewPayloadSchema
>;

const voiceCallFilterMemorySchema = z.object({
    version: z.literal(1),
    filters: voiceCallSavedViewPayloadSchema,
});

export type VoiceCallFilterMemory = z.infer<typeof voiceCallFilterMemorySchema>;

const EMPTY_FILTERS: AppliedFilters = {
    caregiverId: undefined,
    clientId: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    direction: undefined,
    callType: undefined,
    transferredToBackOffice: undefined,
    transferDestinationId: undefined,
    lineNumber: undefined,
    voicemail: undefined,
    notesNeedsReview: undefined,
    assignment: undefined,
    topic: undefined,
    search: undefined,
    officeIds: undefined,
};

function sortOfficeIds(officeIds: ReadonlyArray<string>): Array<string> {
    return [...officeIds].sort((a, b) => a.localeCompare(b));
}

function getOfficeIdsForApi(
    officeIds: ReadonlyArray<string>,
): Array<string> | undefined {
    const sortedOfficeIds = sortOfficeIds(officeIds);
    return sortedOfficeIds.length > 0 ? sortedOfficeIds : undefined;
}

export function getOfficeFilterDisplayValue(
    selectedOfficeIds: ReadonlyArray<string>,
    officeNameById: ReadonlyMap<string, string>,
): string | undefined {
    if (selectedOfficeIds.length === 0) {
        return undefined;
    }
    if (selectedOfficeIds.length === 1) {
        const [selectedOfficeId] = selectedOfficeIds;
        if (selectedOfficeId === undefined) {
            return undefined;
        }
        return officeNameById.get(selectedOfficeId) ?? '1 office';
    }
    return `${selectedOfficeIds.length} offices`;
}

export function getSearchValueForVoiceCallFilters({
    pendingSearch,
    debouncedSearch,
}: {
    readonly pendingSearch: string;
    readonly debouncedSearch: string;
}): string {
    return pendingSearch.trim() === '' ? '' : debouncedSearch;
}

export function buildAppliedVoiceCallFilters(
    pendingFilters: PendingVoiceCallFilters,
): AppliedFilters {
    return {
        caregiverId:
            pendingFilters.caregiverId === ALL_CAREGIVERS_VALUE
                ? undefined
                : pendingFilters.caregiverId,
        clientId:
            pendingFilters.clientId === ALL_CLIENTS_VALUE
                ? undefined
                : pendingFilters.clientId,
        dateFrom: pendingFilters.dateFrom,
        dateTo: pendingFilters.dateTo,
        direction:
            pendingFilters.direction === ALL_DIRECTIONS_VALUE
                ? undefined
                : pendingFilters.direction,
        callType:
            pendingFilters.callType === ALL_CALL_TYPES_VALUE
                ? undefined
                : pendingFilters.callType,
        transferredToBackOffice:
            pendingFilters.transferStatus === 'transferred'
                ? true
                : pendingFilters.transferStatus === 'not_transferred'
                  ? false
                  : undefined,
        transferDestinationId:
            pendingFilters.transferDestinationId ===
            ALL_TRANSFER_DESTINATIONS_VALUE
                ? undefined
                : pendingFilters.transferDestinationId,
        lineNumber:
            pendingFilters.lineNumber === ALL_LINES_VALUE
                ? undefined
                : pendingFilters.lineNumber,
        voicemail:
            pendingFilters.voicemailStatus === 'voicemail'
                ? true
                : pendingFilters.voicemailStatus === 'not_voicemail'
                  ? false
                  : undefined,
        notesNeedsReview:
            pendingFilters.notesFilter === NEEDS_REVIEW_NOTES_VALUE
                ? true
                : undefined,
        assignment:
            pendingFilters.assignment === ALL_ASSIGNMENTS_VALUE
                ? undefined
                : pendingFilters.assignment,
        topic:
            pendingFilters.topic === ALL_TOPICS_VALUE
                ? undefined
                : pendingFilters.topic,
        search: pendingFilters.search.trim() || undefined,
        officeIds: getOfficeIdsForApi(pendingFilters.officeIds),
    };
}

function getAppliedFiltersKey(filters: AppliedFilters): string {
    return JSON.stringify({
        caregiverId: filters.caregiverId ?? null,
        clientId: filters.clientId ?? null,
        dateFrom: filters.dateFrom ?? null,
        dateTo: filters.dateTo ?? null,
        direction: filters.direction ?? null,
        callType: filters.callType ?? null,
        transferredToBackOffice: filters.transferredToBackOffice ?? null,
        transferDestinationId: filters.transferDestinationId ?? null,
        lineNumber: filters.lineNumber ?? null,
        voicemail: filters.voicemail ?? null,
        notesNeedsReview: filters.notesNeedsReview ?? null,
        assignment: filters.assignment ?? null,
        topic: filters.topic ?? null,
        search: filters.search ?? null,
        officeIds: sortOfficeIds(filters.officeIds ?? []),
    });
}

// Search text and the selected call are temporary navigation state. A saved
// view restores only repeatable filters and layout.
export function captureVoiceCallSavedViewPayload(
    filters: AppliedFilters,
    view: 'list' | 'table',
): VoiceCallSavedViewPayload {
    return {
        caregiverId: filters.caregiverId ?? null,
        clientId: filters.clientId ?? null,
        dateFrom: filters.dateFrom ?? null,
        dateTo: filters.dateTo ?? null,
        direction: filters.direction ?? null,
        callType: filters.callType ?? null,
        transferredToBackOffice: filters.transferredToBackOffice ?? null,
        transferDestinationId: filters.transferDestinationId ?? null,
        lineNumber: filters.lineNumber ?? null,
        voicemail: filters.voicemail ?? null,
        notesNeedsReview: filters.notesNeedsReview ?? null,
        assignment: filters.assignment ?? null,
        topic: filters.topic ?? null,
        officeIds: sortOfficeIds(filters.officeIds ?? []),
        view,
    };
}

export function voiceCallSavedViewPayloadsEqual(
    left: VoiceCallSavedViewPayload,
    right: VoiceCallSavedViewPayload,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function appliedFiltersFromVoiceCallSavedView(
    payload: VoiceCallSavedViewPayload,
): AppliedFilters {
    return {
        caregiverId: payload.caregiverId ?? undefined,
        clientId: payload.clientId ?? undefined,
        dateFrom: payload.dateFrom ?? undefined,
        dateTo: payload.dateTo ?? undefined,
        direction: payload.direction ?? undefined,
        callType: payload.callType ?? undefined,
        transferredToBackOffice: payload.transferredToBackOffice ?? undefined,
        transferDestinationId: payload.transferDestinationId ?? undefined,
        lineNumber: payload.lineNumber ?? undefined,
        voicemail: payload.voicemail ?? undefined,
        notesNeedsReview: payload.notesNeedsReview ?? undefined,
        assignment: payload.assignment ?? undefined,
        topic: payload.topic ?? undefined,
        search: undefined,
        officeIds: payload.officeIds.length > 0 ? payload.officeIds : undefined,
    };
}

function formatClientName(client: {
    readonly first_name?: string | null;
    readonly last_name?: string | null;
    readonly preferred_name?: string | null;
}): string {
    const first = client.preferred_name ?? client.first_name ?? '';
    const last = client.last_name ?? '';
    const combined = `${first} ${last}`.trim();
    return combined || 'Unnamed client';
}

function ClientFilterCombobox({
    organizationId,
    value,
    onValueChange,
}: {
    readonly organizationId: string;
    readonly value: string;
    readonly onValueChange: (value: string) => void;
}) {
    const { formatName } = useFormatPersonName();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedName, setSelectedName] = useState<string | null>(null);

    const { data, isLoading } = useVoiceCallFilterParticipants(organizationId, {
        reason: 'voice-calls-client-filter',
    });
    const clients = useMemo(() => {
        const allClients = data?.clients ?? [];
        if (!search) {
            return allClients;
        }
        const normalized = search.toLowerCase();
        return allClients.filter((c) => {
            const first = c.first_name ?? '';
            const last = c.last_name ?? '';
            const preferred = c.preferred_name ?? '';
            return `${first} ${last} ${preferred}`
                .toLowerCase()
                .includes(normalized);
        });
    }, [data?.clients, search]);

    const displayValue = value === ALL_CLIENTS_VALUE ? undefined : selectedName;

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="All clients"
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search clients…"
            contentClassName="w-[300px]"
            matchTriggerWidth={false}
            fullWidth={false}
            triggerClassName="h-9 w-full text-sm"
            commandProps={{ shouldFilter: false }}
        >
            <CommandEmpty>
                {isLoading ? 'Loading…' : 'No clients found.'}
            </CommandEmpty>
            <CommandGroup>
                <CommandItem
                    value={ALL_CLIENTS_VALUE}
                    onSelect={() => {
                        onValueChange(ALL_CLIENTS_VALUE);
                        setSelectedName(null);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            value === ALL_CLIENTS_VALUE
                                ? 'opacity-100'
                                : 'opacity-0',
                        )}
                    />
                    All clients
                </CommandItem>
                {clients.map((clientRecord) => {
                    const name = formatName(formatClientName(clientRecord));
                    return (
                        <CommandItem
                            key={clientRecord.id}
                            value={clientRecord.id}
                            onSelect={(currentValue) => {
                                onValueChange(currentValue);
                                setSelectedName(name);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    value === clientRecord.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            {name}
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

function CaregiverFilterCombobox({
    organizationId,
    value,
    onValueChange,
}: {
    readonly organizationId: string;
    readonly value: string;
    readonly onValueChange: (value: string) => void;
}) {
    const { formatName } = useFormatPersonName();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedName, setSelectedName] = useState<string | null>(null);

    const { data, isLoading } = useVoiceCallFilterParticipants(organizationId, {
        reason: 'voice-calls-caregiver-filter',
    });
    const caregivers = useMemo(() => {
        const allCaregivers = data?.caregivers ?? [];
        if (!search) {
            return allCaregivers;
        }
        const normalized = search.toLowerCase();
        return allCaregivers.filter((c) =>
            c.name.toLowerCase().includes(normalized),
        );
    }, [data?.caregivers, search]);

    const displayValue =
        value === ALL_CAREGIVERS_VALUE ? undefined : selectedName;

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="All caregivers"
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search caregivers…"
            contentClassName="w-[300px]"
            matchTriggerWidth={false}
            fullWidth={false}
            triggerClassName="h-9 w-full text-sm"
            commandProps={{ shouldFilter: false }}
        >
            <CommandEmpty>
                {isLoading ? 'Loading…' : 'No caregivers found.'}
            </CommandEmpty>
            <CommandGroup>
                <CommandItem
                    value={ALL_CAREGIVERS_VALUE}
                    onSelect={() => {
                        onValueChange(ALL_CAREGIVERS_VALUE);
                        setSelectedName(null);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            value === ALL_CAREGIVERS_VALUE
                                ? 'opacity-100'
                                : 'opacity-0',
                        )}
                    />
                    All caregivers
                </CommandItem>
                {(caregivers ?? []).map((caregiver) => (
                    <CommandItem
                        key={caregiver.id}
                        value={caregiver.id}
                        onSelect={(currentValue) => {
                            onValueChange(currentValue);
                            setSelectedName(formatName(caregiver.name));
                            setOpen(false);
                        }}
                    >
                        <Check
                            className={cn(
                                'mr-2 h-4 w-4',
                                value === caregiver.id
                                    ? 'opacity-100'
                                    : 'opacity-0',
                            )}
                        />
                        {formatName(caregiver.name)}
                    </CommandItem>
                ))}
            </CommandGroup>
        </Combobox>
    );
}

export function OfficeFilterCombobox({
    offices,
    selectedOfficeIds,
    onSelectedOfficeIdsChange,
}: {
    readonly offices: ReadonlyArray<OrganizationOffice>;
    readonly selectedOfficeIds: ReadonlyArray<string>;
    readonly onSelectedOfficeIdsChange: (
        officeIds: ReadonlyArray<string>,
    ) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const officeNameById = useMemo(
        () =>
            new Map(offices.map((office) => [office.id, office.display_name])),
        [offices],
    );
    const displayValue = getOfficeFilterDisplayValue(
        selectedOfficeIds,
        officeNameById,
    );

    function toggleOffice(officeId: string) {
        onSelectedOfficeIdsChange(
            selectedOfficeIds.includes(officeId)
                ? selectedOfficeIds.filter((id) => id !== officeId)
                : [...selectedOfficeIds, officeId],
        );
    }

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="All offices"
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search offices…"
            contentClassName="w-[260px]"
            matchTriggerWidth={false}
            fullWidth={false}
            triggerClassName="h-9 w-[150px] text-sm"
            triggerProps={{ 'aria-label': 'Office' }}
        >
            <CommandEmpty>No offices found.</CommandEmpty>
            <CommandGroup>
                <CommandItem
                    value={ALL_OFFICES_VALUE}
                    onSelect={() => {
                        onSelectedOfficeIdsChange([]);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            selectedOfficeIds.length === 0
                                ? 'opacity-100'
                                : 'opacity-0',
                        )}
                    />
                    All offices
                </CommandItem>
                {offices.map((office) => {
                    const isSelected = selectedOfficeIds.includes(office.id);
                    return (
                        <CommandItem
                            key={office.id}
                            value={office.display_name}
                            onSelect={() => toggleOffice(office.id)}
                        >
                            <div
                                className={cn(
                                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                                    isSelected
                                        ? 'border-foreground bg-foreground'
                                        : 'border-muted-foreground bg-background',
                                )}
                            >
                                <Check
                                    className={cn(
                                        'h-4 w-4',
                                        isSelected
                                            ? 'text-primary-foreground'
                                            : 'invisible',
                                    )}
                                />
                            </div>
                            <span className="truncate">
                                {office.display_name}
                            </span>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

const voiceCallsSearchSchema = z.object({
    direction: z.enum(['inbound', 'outbound']).optional(),
    voiceCallId: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
    view: z.enum(['list', 'table']).optional(),
});

export const Route = createFileRoute('/_app/(main)/voice-calls')({
    component: VoiceCallsRoute,
    validateSearch: voiceCallsSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Voice Calls | Phoebe',
            },
        ],
    }),
});

function VoiceCallsRoute() {
    const { formatName } = useFormatPersonName();
    const router = useRouter();
    const navigate = Route.useNavigate();
    const {
        direction: directionFromSearch,
        voiceCallId: voiceCallIdFromSearch,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
        view: viewFromSearch,
    } = Route.useSearch();
    const posthog = usePostHog();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();
    const mode = useMode() ?? 'live';
    const { timezone } = useOrganizationTimezone();
    const { data: user } = useUser({
        reason: 'calls-page',
    });
    const { data: organization } = useOrganization(organizationId, {
        reason: 'calls-page-table-view-flag',
    });
    const uiV3ComponentsEnabled = useFeatureFlag(
        'ui_v3_components',
        organization?.features,
    );
    const isTableViewFlagEnabled = useFeatureFlag(
        'voice_calls_table_view_enabled',
        organization?.features,
    );
    const isAssignmentEnabled = useFeatureFlag(
        'voice_call_assignment_enabled',
        organization?.features,
    );
    const isConversationOwnershipEnabled = useFeatureFlag(
        'conversation_ownership_enabled',
        organization?.features,
    );
    const silentListeningLabelSettingsEnabled = false;
    const isTableViewFlagLoading = organization === undefined;
    // When the flag is off the page always shows the classic list, ignoring the
    // `view` param. When on, the feed is still the default; the dense table is
    // opt-in via the `view` param.
    const resolvedView: 'list' | 'table' = !isTableViewFlagEnabled
        ? 'list'
        : (viewFromSearch ?? 'list');
    const isTableView = resolvedView === 'table';
    const isMobile = useIsMobile();

    // Pending filter state (not yet applied)
    const [pendingCaregiverId, setPendingCaregiverId] =
        useState(ALL_CAREGIVERS_VALUE);
    const [pendingClientId, setPendingClientId] = useState(ALL_CLIENTS_VALUE);
    const [pendingDateFrom, setPendingDateFrom] = useState<string | undefined>(
        undefined,
    );
    const [pendingDateTo, setPendingDateTo] = useState<string | undefined>(
        undefined,
    );
    const [pendingDirection, setPendingDirection] =
        useState<CallDirectionFilterValue>(
            directionFromSearch ?? ALL_DIRECTIONS_VALUE,
        );
    const [pendingCallType, setPendingCallType] =
        useState<CallTypeFilterValue>(ALL_CALL_TYPES_VALUE);
    const [pendingTransferStatus, setPendingTransferStatus] =
        useState<TransferStatusFilterValue>(ALL_TRANSFER_STATUSES_VALUE);
    const [pendingTransferDestinationId, setPendingTransferDestinationId] =
        useState(ALL_TRANSFER_DESTINATIONS_VALUE);
    const [pendingLineNumber, setPendingLineNumber] = useState(ALL_LINES_VALUE);
    const [pendingVoicemailStatus, setPendingVoicemailStatus] =
        useState<VoicemailStatusFilterValue>(ALL_VOICEMAIL_STATUSES_VALUE);
    const [pendingNotesFilter, setPendingNotesFilter] =
        useState<NotesFilterValue>(ALL_NOTES_VALUE);
    const [pendingAssignment, setPendingAssignment] =
        useState<AssignmentFilterValue>(ALL_ASSIGNMENTS_VALUE);
    const [pendingTopic, setPendingTopic] =
        useState<CallTopicFilterValue>(ALL_TOPICS_VALUE);
    const [pendingSearch, setPendingSearch] = useState('');
    const debouncedSearch = useDebounce(
        pendingSearch.trim(),
        SEARCH_DEBOUNCE_DELAY_MS,
    );
    const [pendingOfficeIds, setPendingOfficeIds] = useState<
        ReadonlyArray<string>
    >([]);

    // Applied filter state (sent to backend)
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
        ...EMPTY_FILTERS,
        direction: directionFromSearch,
    });
    const [activeSavedViewId, setActiveSavedViewId] = useState<string | null>(
        null,
    );
    const appliedDirectionLinkRef = useRef<string | null>(null);
    const savedViewStorageKey =
        user?.id && organizationId
            ? localSavedViewsStorageKey('voice-calls', organizationId, user.id)
            : null;
    const filterMemoryStorageKey =
        user?.id && organizationId
            ? localFilterMemoryStorageKey(
                  'voice-calls',
                  organizationId,
                  user.id,
                  mode,
              )
            : null;
    const savedViews = useLocalSavedViews({
        storageKey: savedViewStorageKey,
        payloadSchema: voiceCallSavedViewPayloadSchema,
    });
    const currentSavedViewPayload = useMemo(
        () => captureVoiceCallSavedViewPayload(appliedFilters, resolvedView),
        [appliedFilters, resolvedView],
    );
    const setPendingFiltersFromApplied = useCallback(
        (filters: AppliedFilters) => {
            setPendingCaregiverId(filters.caregiverId ?? ALL_CAREGIVERS_VALUE);
            setPendingClientId(filters.clientId ?? ALL_CLIENTS_VALUE);
            setPendingDateFrom(filters.dateFrom);
            setPendingDateTo(filters.dateTo);
            setPendingDirection(filters.direction ?? ALL_DIRECTIONS_VALUE);
            setPendingCallType(filters.callType ?? ALL_CALL_TYPES_VALUE);
            setPendingTransferStatus(
                filters.transferredToBackOffice === true
                    ? 'transferred'
                    : filters.transferredToBackOffice === false
                      ? 'not_transferred'
                      : ALL_TRANSFER_STATUSES_VALUE,
            );
            setPendingTransferDestinationId(
                filters.transferDestinationId ??
                    ALL_TRANSFER_DESTINATIONS_VALUE,
            );
            setPendingLineNumber(filters.lineNumber ?? ALL_LINES_VALUE);
            setPendingVoicemailStatus(
                filters.voicemail === true
                    ? 'voicemail'
                    : filters.voicemail === false
                      ? 'not_voicemail'
                      : ALL_VOICEMAIL_STATUSES_VALUE,
            );
            setPendingNotesFilter(
                filters.notesNeedsReview
                    ? NEEDS_REVIEW_NOTES_VALUE
                    : ALL_NOTES_VALUE,
            );
            setPendingAssignment(filters.assignment ?? ALL_ASSIGNMENTS_VALUE);
            setPendingTopic(filters.topic ?? ALL_TOPICS_VALUE);
            setPendingSearch('');
            setPendingOfficeIds(filters.officeIds ?? []);
        },
        [],
    );

    const filterMemoryValue = useMemo<VoiceCallFilterMemory>(
        () => ({ version: 1, filters: currentSavedViewPayload }),
        [currentSavedViewPayload],
    );
    const { isHydrated: isFilterMemoryHydrated } = useLocalFilterMemory({
        storageKey: filterMemoryStorageKey,
        schema: voiceCallFilterMemorySchema,
        value: filterMemoryValue,
        defaultValue: () => ({
            version: 1,
            filters: captureVoiceCallSavedViewPayload(
                {
                    ...EMPTY_FILTERS,
                    direction: directionFromSearch,
                },
                resolvedView,
            ),
        }),
        onRestore: (memory) => {
            const rememberedFilters = appliedFiltersFromVoiceCallSavedView(
                memory.filters,
            );
            const nextFilters = {
                ...rememberedFilters,
                // An explicit inbound/outbound link wins for this visit.
                direction: directionFromSearch ?? rememberedFilters.direction,
            };
            setPendingFiltersFromApplied(nextFilters);
            setAppliedFilters(nextFilters);
            setActiveSavedViewId(null);
        },
    });
    useEffect(() => {
        if (!isFilterMemoryHydrated) {
            return;
        }
        if (directionFromSearch === undefined) {
            appliedDirectionLinkRef.current = null;
            return;
        }
        const directionLinkKey = `${filterMemoryStorageKey}:${directionFromSearch}`;
        if (appliedDirectionLinkRef.current === directionLinkKey) {
            return;
        }
        appliedDirectionLinkRef.current = directionLinkKey;
        if (appliedFilters.direction === directionFromSearch) {
            return;
        }
        const nextFilters = {
            ...appliedFilters,
            direction: directionFromSearch,
        };
        setPendingFiltersFromApplied(nextFilters);
        setAppliedFilters(nextFilters);
        setActiveSavedViewId(null);
    }, [
        appliedFilters,
        directionFromSearch,
        filterMemoryStorageKey,
        isFilterMemoryHydrated,
        setPendingFiltersFromApplied,
    ]);

    const [selectedCallId, setSelectedCallId] = useState<string | null>(
        voiceCallIdFromSearch ?? null,
    );
    const [mobileDialogCallId, setMobileDialogCallId] = useState<string | null>(
        null,
    );
    const pendingFiltersForApi = useMemo(
        () =>
            buildAppliedVoiceCallFilters({
                caregiverId: pendingCaregiverId,
                clientId: pendingClientId,
                dateFrom: pendingDateFrom,
                dateTo: pendingDateTo,
                direction: pendingDirection,
                callType: pendingCallType,
                transferStatus: pendingTransferStatus,
                transferDestinationId: pendingTransferDestinationId,
                lineNumber: pendingLineNumber,
                voicemailStatus: pendingVoicemailStatus,
                notesFilter: pendingNotesFilter,
                assignment: pendingAssignment,
                topic: pendingTopic,
                search: getSearchValueForVoiceCallFilters({
                    pendingSearch,
                    debouncedSearch,
                }),
                officeIds: pendingOfficeIds,
            }),
        [
            debouncedSearch,
            pendingAssignment,
            pendingCallType,
            pendingCaregiverId,
            pendingClientId,
            pendingDateFrom,
            pendingDateTo,
            pendingDirection,
            pendingLineNumber,
            pendingNotesFilter,
            pendingOfficeIds,
            pendingSearch,
            pendingTopic,
            pendingTransferDestinationId,
            pendingTransferStatus,
            pendingVoicemailStatus,
        ],
    );
    const hasFilterChanges =
        getAppliedFiltersKey(pendingFiltersForApi) !==
        getAppliedFiltersKey(appliedFilters);
    const hasActiveFilters =
        appliedFilters.caregiverId !== undefined ||
        appliedFilters.clientId !== undefined ||
        appliedFilters.dateFrom !== undefined ||
        appliedFilters.dateTo !== undefined ||
        appliedFilters.direction !== undefined ||
        appliedFilters.callType !== undefined ||
        appliedFilters.transferredToBackOffice !== undefined ||
        appliedFilters.transferDestinationId !== undefined ||
        appliedFilters.lineNumber !== undefined ||
        appliedFilters.voicemail !== undefined ||
        appliedFilters.notesNeedsReview !== undefined ||
        appliedFilters.assignment !== undefined ||
        appliedFilters.topic !== undefined ||
        appliedFilters.search !== undefined ||
        appliedFilters.officeIds !== undefined;

    const voiceCallFilters = useMemo(
        () => ({
            direction: appliedFilters.direction,
            caregiver_id: appliedFilters.caregiverId,
            client_id: appliedFilters.clientId,
            date_from: appliedFilters.dateFrom,
            date_to: appliedFilters.dateTo,
            call_type: appliedFilters.callType,
            transferred_to_back_office: appliedFilters.transferredToBackOffice,
            transfer_destination_id: appliedFilters.transferDestinationId,
            line_number: appliedFilters.lineNumber,
            voicemail: appliedFilters.voicemail,
            notes_needs_review: appliedFilters.notesNeedsReview,
            assignment: appliedFilters.assignment,
            topic: appliedFilters.topic,
            search: appliedFilters.search,
            office_id: appliedFilters.officeIds
                ? [...appliedFilters.officeIds]
                : undefined,
        }),
        [appliedFilters],
    );
    const voiceCallFiltersReady = isFilterMemoryHydrated;

    const {
        calls: pageCalls,
        error: callsError,
        isLoading: isLoadingCalls,
        isLoadingMore: isLoadingMoreCalls,
        isValidating: isValidatingCalls,
        hasMore: hasMoreCalls,
        canSearchOlderCalls,
        loadMore: loadMoreCalls,
        refreshFirstPage: refreshFirstCallsPage,
        refreshCalls,
    } = useInfiniteVoiceCalls(
        voiceCallFiltersReady ? organizationId : null,
        { reason: 'calls-page' },
        CALLS_PAGE_SIZE,
        voiceCallFilters,
    );
    const isPageLoading = isVoiceCallsPageLoading(
        voiceCallFiltersReady,
        isLoadingCalls,
    );
    const { data: offices } = useOrganizationOffices(organizationId, {
        reason: 'voice-calls-offices',
    });
    const officeOptions = useMemo(
        () =>
            [...(offices ?? [])].sort((a, b) =>
                a.display_name.localeCompare(b.display_name),
            ),
        [offices],
    );
    const { data: callRoutingDestinations } =
        useOrganizationCallRoutingDestinations(organizationId);
    const transferDestinationOptions = useMemo(
        () =>
            (callRoutingDestinations ?? []).filter(
                (destination) => destination.is_active,
            ),
        [callRoutingDestinations],
    );

    // Resolve caregiver/client display names for the active-filter chips. The
    // popover comboboxes fetch the same participant list, so this SWR call is
    // deduped and does not add a meaningful extra request.
    const { data: filterParticipants } = useVoiceCallFilterParticipants(
        organizationId,
        { reason: 'voice-calls-active-filter-chips' },
    );
    const lineOptions = useMemo(
        () => filterParticipants?.lines ?? [],
        [filterParticipants?.lines],
    );
    const caregiverNameById = useMemo(
        () =>
            new Map(
                (filterParticipants?.caregivers ?? []).map((caregiver) => [
                    caregiver.id,
                    caregiver.name,
                ]),
            ),
        [filterParticipants?.caregivers],
    );
    const clientNameById = useMemo(
        () =>
            new Map(
                (filterParticipants?.clients ?? []).map((client) => [
                    client.id,
                    formatName(formatClientName(client)),
                ]),
            ),
        [filterParticipants?.clients, formatName],
    );

    // Chips for the secondary filters that live inside the "Filters" popover, so
    // active hidden filters stay visible and individually removable. Resetting a
    // chip's pending value auto-applies via the filter-sync effect below.
    const secondaryFilterChips = useMemo(() => {
        const chips: Array<{
            readonly key: string;
            readonly label: string;
            readonly value: string;
            readonly onRemove: () => void;
        }> = [];
        if (uiV3ComponentsEnabled && pendingDateFrom) {
            chips.push({
                key: 'dateFrom',
                label: 'Start date',
                value: formatDateKeyLong(pendingDateFrom, timezone),
                onRemove: () => setPendingDateFrom(undefined),
            });
        }
        if (uiV3ComponentsEnabled && pendingDateTo) {
            chips.push({
                key: 'dateTo',
                label: 'End date',
                value: formatDateKeyLong(pendingDateTo, timezone),
                onRemove: () => setPendingDateTo(undefined),
            });
        }
        if (pendingCallType !== ALL_CALL_TYPES_VALUE) {
            chips.push({
                key: 'callType',
                label: 'Call type',
                value:
                    pendingCallType === 'notification'
                        ? 'Notification calls'
                        : pendingCallType === 'client'
                          ? 'Client'
                          : 'Caregiver',
                onRemove: () => setPendingCallType(ALL_CALL_TYPES_VALUE),
            });
        }
        if (pendingTransferStatus !== ALL_TRANSFER_STATUSES_VALUE) {
            chips.push({
                key: 'transfer',
                label: 'Transfer',
                value:
                    pendingTransferStatus === 'transferred'
                        ? 'Transferred'
                        : 'Not transferred',
                onRemove: () =>
                    setPendingTransferStatus(ALL_TRANSFER_STATUSES_VALUE),
            });
        }
        // Always surface a chip while the filter is applied — even if the
        // matching popover control is hidden (no active destinations) — so the
        // applied filter stays counted in the badge and individually clearable.
        if (pendingTransferDestinationId !== ALL_TRANSFER_DESTINATIONS_VALUE) {
            const destination = transferDestinationOptions.find(
                (option) => option.id === pendingTransferDestinationId,
            );
            chips.push({
                key: 'transferDestination',
                label: 'Transferred to',
                value: destination?.name ?? 'Selected',
                onRemove: () =>
                    setPendingTransferDestinationId(
                        ALL_TRANSFER_DESTINATIONS_VALUE,
                    ),
            });
        }
        // The Line control lives in the visible filter bar, so it normally
        // needs no chip. But it is hidden when the organization has fewer than
        // two lines, and a restored saved view can carry a line the dropdown
        // no longer offers — in both cases surface a chip so the applied
        // filter stays visible and individually removable.
        if (pendingLineNumber !== ALL_LINES_VALUE) {
            const line = lineOptions.find(
                (option) => option.number === pendingLineNumber,
            );
            if (lineOptions.length <= 1 || !line) {
                chips.push({
                    key: 'line',
                    label: 'Line',
                    value:
                        line?.label?.trim() ||
                        formatPhoneNumber(pendingLineNumber) ||
                        pendingLineNumber,
                    onRemove: () => setPendingLineNumber(ALL_LINES_VALUE),
                });
            }
        }
        if (pendingVoicemailStatus !== ALL_VOICEMAIL_STATUSES_VALUE) {
            chips.push({
                key: 'voicemail',
                label: 'Voicemail',
                value:
                    pendingVoicemailStatus === 'voicemail'
                        ? 'Voicemail'
                        : 'No voicemail',
                onRemove: () =>
                    setPendingVoicemailStatus(ALL_VOICEMAIL_STATUSES_VALUE),
            });
        }
        if (pendingNotesFilter !== ALL_NOTES_VALUE) {
            chips.push({
                key: 'notes',
                label: 'Notes',
                value: 'Needs review',
                onRemove: () => setPendingNotesFilter(ALL_NOTES_VALUE),
            });
        }
        if (pendingTopic !== ALL_TOPICS_VALUE) {
            const topicOption = CALL_TOPIC_OPTIONS.find(
                (option) => option.value === pendingTopic,
            );
            chips.push({
                key: 'topic',
                label: 'Topic',
                value: topicOption?.label ?? pendingTopic,
                onRemove: () => setPendingTopic(ALL_TOPICS_VALUE),
            });
        }
        if (pendingCaregiverId !== ALL_CAREGIVERS_VALUE) {
            chips.push({
                key: 'caregiver',
                label: 'Caregiver',
                value: caregiverNameById.get(pendingCaregiverId) ?? 'Selected',
                onRemove: () => setPendingCaregiverId(ALL_CAREGIVERS_VALUE),
            });
        }
        if (pendingClientId !== ALL_CLIENTS_VALUE) {
            chips.push({
                key: 'client',
                label: 'Client',
                value: clientNameById.get(pendingClientId) ?? 'Selected',
                onRemove: () => setPendingClientId(ALL_CLIENTS_VALUE),
            });
        }
        return chips;
    }, [
        caregiverNameById,
        clientNameById,
        lineOptions,
        uiV3ComponentsEnabled,
        pendingDateFrom,
        pendingDateTo,
        timezone,
        pendingCallType,
        pendingCaregiverId,
        pendingClientId,
        pendingLineNumber,
        pendingNotesFilter,
        pendingTopic,
        pendingTransferDestinationId,
        pendingTransferStatus,
        pendingVoicemailStatus,
        transferDestinationOptions,
    ]);
    const secondaryFilterCount = secondaryFilterChips.length;

    // Write the selected call into the `voiceCallId` URL search param so each
    // call has a shareable, bookmarkable URL and browser back/forward works.
    // Other search params are preserved. Auto-selection and filter resets use
    // `replace` so they do not create extra history entries.
    const selectVoiceCall = useCallback(
        (callId: string | null, options?: { readonly replace?: boolean }) => {
            void navigate({
                to: '/voice-calls',
                search: (previous) => ({
                    ...previous,
                    voiceCallId: callId ?? undefined,
                }),
                replace: options?.replace ?? false,
            });
        },
        [navigate],
    );

    const handleViewChange = useCallback(
        (nextView: string) => {
            if (nextView !== 'list' && nextView !== 'table') {
                return;
            }
            posthog?.capture(EVENTS.VOICE_CALLS_VIEW_SWITCHED, {
                view: nextView,
            });
            void navigate({
                to: '/voice-calls',
                search: (previous) => ({ ...previous, view: nextView }),
                replace: true,
            });
        },
        [navigate, posthog],
    );

    // Keep the selected call in sync with the `voiceCallId` URL param so
    // deep links and browser back/forward navigation update the UI.
    useEffect(() => {
        // Both mobile and the desktop table view open details in a dialog; the
        // classic desktop list uses the inline right-hand detail pane instead.
        if (isMobile || isTableView) {
            setMobileDialogCallId(voiceCallIdFromSearch ?? null);
            return;
        }
        setSelectedCallId(voiceCallIdFromSearch ?? null);
    }, [isMobile, isTableView, voiceCallIdFromSearch]);

    useOnModeChange(() => {
        // Filter memory is mode-scoped and restores the destination mode.
        selectVoiceCall(null, { replace: true });
    });

    // Auto-select the first call on desktop when no call is deep-linked. The
    // table view opens details in a dialog, so we never auto-open one there.
    useEffect(() => {
        if (voiceCallIdFromSearch) {
            return;
        }
        if (
            isMobile ||
            isValidatingCalls ||
            isTableView ||
            isTableViewFlagLoading
        ) {
            return;
        }

        if (pageCalls.length === 0) {
            return;
        }

        const [firstCall] = pageCalls;
        if (!firstCall) {
            return;
        }

        selectVoiceCall(firstCall.id, { replace: true });
    }, [
        isMobile,
        isTableView,
        isTableViewFlagLoading,
        isValidatingCalls,
        pageCalls,
        selectVoiceCall,
        voiceCallIdFromSearch,
    ]);

    // PHO-10343: when a deep-linked call already lives in the loaded pages,
    // scroll the sidebar to its row once. The detail pane loads independently
    // via useOrganizationVoiceCall, so we don't auto-paginate to find calls
    // older than the loaded window — doing so used to hammer the API and,
    // when SWR returned cached pages synchronously, exceed React's max
    // update depth (React #185).
    const scrolledToCallIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectedCallId || isMobile) {
            return;
        }
        if (scrolledToCallIdRef.current === selectedCallId) {
            return;
        }
        const row = document.getElementById(`voice-call-row-${selectedCallId}`);
        if (!row) {
            return;
        }
        row.scrollIntoView({ block: 'nearest' });
        scrolledToCallIdRef.current = selectedCallId;
    }, [selectedCallId, isMobile, pageCalls]);

    const selectedCallListItem = useMemo(() => {
        if (!selectedCallId || isMobile) {
            return null;
        }

        return pageCalls.find((call) => call.id === selectedCallId) ?? null;
    }, [isMobile, pageCalls, selectedCallId]);
    const shouldFetchSelectedCallDetail =
        (!isMobile && selectedCallId !== null) || !!voiceCallIdFromSearch;
    const voiceCallIdForDetail = shouldFetchSelectedCallDetail
        ? (selectedCallId ?? voiceCallIdFromSearch ?? null)
        : null;
    const {
        data: selectedCallDetail,
        error: selectedCallDetailError,
        isLoading: isLoadingSelectedCallDetail,
        mutate: mutateSelectedCallDetail,
    } = useOrganizationVoiceCall(organizationId, voiceCallIdForDetail, {
        reason: 'calls-page-detail',
    });
    const isMissingVoiceCallInCurrentOrganization =
        !!voiceCallIdFromSearch &&
        selectedCallDetailError instanceof ApiError &&
        selectedCallDetailError.status === 404;
    const { isResolvingAcrossOrganizations } = useResourceOrganizationLookup({
        enabled: isMissingVoiceCallInCurrentOrganization,
        resourceId: voiceCallIdFromSearch,
        resourceType: 'voice_call',
    });
    // List payloads omit full call fields; the detail endpoint refetch keeps
    // recordings, SOT transcripts, and actions current without bloating lists.
    const selectedCall = selectedCallDetail ?? selectedCallListItem;

    const mobileDialogCall = useMemo(() => {
        if (!mobileDialogCallId) {
            return null;
        }

        return pageCalls.find((call) => call.id === mobileDialogCallId) ?? null;
    }, [mobileDialogCallId, pageCalls]);

    useVoiceCallRealtimeRefresh({
        organizationId,
        selectedCallId,
        // Newly created calls only affect the newest page; revalidating every
        // historical page would multiply the office-filter query again.
        refreshCallsPage: () => refreshFirstCallsPage(),
        refreshSelectedCallDetail: () => mutateSelectedCallDetail(),
    });

    const recordingVoiceCallIds = useMemo(() => {
        if (
            selectedCall?.kind === 'notification' ||
            !selectedCall?.recording_url
        ) {
            return null;
        }
        if (!isS3RecordingUrl(selectedCall.recording_url)) {
            return null;
        }
        return [selectedCall.id];
    }, [selectedCall]);
    const {
        data: recordingUrls,
        error: recordingUrlsError,
        isLoading: isRecordingUrlLoading,
    } = useVoiceCallRecordingUrls(organizationId, recordingVoiceCallIds);
    const handleSelectCall = (callId: string) => {
        selectVoiceCall(callId);
    };
    const handleSelectTableCall = (callId: string) => {
        posthog?.capture(EVENTS.VOICE_CALLS_TABLE_ROW_OPENED, {
            voice_call_id: callId,
        });
        // With the new conversations experience enabled, a caregiver call
        // lives inside that caregiver's conversation thread — jump straight
        // there instead of opening the call detail dialog. Calls without a
        // caregiver (client/unknown callers) keep the dialog.
        const call = pageCalls.find((pageCall) => pageCall.id === callId);
        if (
            isConversationOwnershipEnabled &&
            call?.kind !== 'notification' &&
            call?.caregiver_id
        ) {
            void navigate({
                to: '/conversations',
                search: { caregiverId: call.caregiver_id },
            });
            return;
        }
        selectVoiceCall(callId);
    };
    const refreshCallData = async (voiceCallId: string) => {
        await refreshCalls([voiceCallId]);
        await mutateSelectedCallDetail();
    };
    const handleBack = () => {
        if (window.history.length > 1) {
            window.history.back();
            return;
        }

        void router.navigate({ to: '/home' });
    };

    // Load the next page once the user scrolls within ~200px of the bottom.
    const handleCallListScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (canSearchOlderCalls) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const distanceToBottomPx = scrollHeight - scrollTop - clientHeight;
        if (distanceToBottomPx <= INFINITE_SCROLL_THRESHOLD_PX) {
            loadMoreCalls();
        }
    };

    useEffect(() => {
        if (!hasFilterChanges) {
            return;
        }

        setAppliedFilters({
            ...pendingFiltersForApi,
        });
        selectVoiceCall(null, { replace: true });
    }, [hasFilterChanges, pendingFiltersForApi, selectVoiceCall]);

    // The assignment presets only render in the table view (the Assigned
    // column lives there), so clear the filter when leaving it — otherwise
    // it would keep constraining the feed with no visible control.
    useEffect(() => {
        if (!isTableView) {
            setPendingAssignment(ALL_ASSIGNMENTS_VALUE);
        }
    }, [isTableView]);

    const handleClearFilters = () => {
        setPendingCaregiverId(ALL_CAREGIVERS_VALUE);
        setPendingClientId(ALL_CLIENTS_VALUE);
        setPendingDateFrom(undefined);
        setPendingDateTo(undefined);
        setPendingDirection(ALL_DIRECTIONS_VALUE);
        setPendingCallType(ALL_CALL_TYPES_VALUE);
        setPendingTransferStatus(ALL_TRANSFER_STATUSES_VALUE);
        setPendingTransferDestinationId(ALL_TRANSFER_DESTINATIONS_VALUE);
        setPendingLineNumber(ALL_LINES_VALUE);
        setPendingVoicemailStatus(ALL_VOICEMAIL_STATUSES_VALUE);
        setPendingNotesFilter(ALL_NOTES_VALUE);
        setPendingAssignment(ALL_ASSIGNMENTS_VALUE);
        setPendingTopic(ALL_TOPICS_VALUE);
        setPendingSearch('');
        setPendingOfficeIds([]);
        setAppliedFilters({
            ...EMPTY_FILTERS,
            direction: undefined,
        });
        setActiveSavedViewId(null);
        selectVoiceCall(null, { replace: true });
    };

    const applySavedView = (savedViewPayload: VoiceCallSavedViewPayload) => {
        const nextFilters =
            appliedFiltersFromVoiceCallSavedView(savedViewPayload);

        setPendingFiltersFromApplied(nextFilters);
        setAppliedFilters(nextFilters);
        const nextView = isTableViewFlagEnabled
            ? savedViewPayload.view
            : 'list';
        posthog?.capture(EVENTS.VOICE_CALLS_VIEW_SWITCHED, {
            view: nextView,
        });
        void navigate({
            to: '/voice-calls',
            search: (previous) => ({
                ...previous,
                direction: nextFilters.direction,
                voiceCallId: undefined,
                view: nextView,
            }),
            replace: true,
        });
    };

    if (isWaitingForDeepLinkOrganization || !organizationId) {
        return null;
    }

    const mobileDialogCallerName = mobileDialogCall?.caller_name ?? null;
    const selectedCallCaregiverName =
        selectedCallListItem?.caller_name ??
        selectedCallDetail?.caller_name ??
        null;
    const dateFilters = (
        <Fragment>
            <div
                className={cn(
                    'flex min-w-0 flex-col gap-1',
                    !uiV3ComponentsEnabled && 'w-[160px]',
                )}
            >
                <DatePicker
                    value={pendingDateFrom}
                    onChange={(v) => {
                        const next = v || undefined;
                        setPendingDateFrom(next);
                        if (next && pendingDateTo && pendingDateTo < next) {
                            setPendingDateTo(undefined);
                        }
                    }}
                    placeholder="Start date"
                />
            </div>
            <div
                className={cn(
                    'flex min-w-0 flex-col gap-1',
                    !uiV3ComponentsEnabled && 'w-[160px]',
                )}
            >
                <DatePicker
                    value={pendingDateTo}
                    onChange={(v) => {
                        const next = v || undefined;
                        if (next && pendingDateFrom && next < pendingDateFrom) {
                            return;
                        }
                        setPendingDateTo(next);
                    }}
                    placeholder="End date"
                />
            </div>
        </Fragment>
    );
    const headerActions = (
        <Fragment>
            <TrainingModePageTourReplayButton />
            {isTableViewFlagEnabled && (
                <Tabs value={resolvedView} onValueChange={handleViewChange}>
                    <TabsList>
                        <TabsTrigger value="list">Feed</TabsTrigger>
                        <TabsTrigger value="table">List</TabsTrigger>
                    </TabsList>
                </Tabs>
            )}
        </Fragment>
    );

    const selectedCallDetailContent = !selectedCallId ? (
        <p className="text-muted-foreground text-sm">
            Select a call to view details.
        </p>
    ) : (isLoadingSelectedCallDetail || isResolvingAcrossOrganizations) &&
      !selectedCall ? (
        <div className="flex items-center justify-center py-12">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </div>
    ) : selectedCallDetailError && !selectedCall ? (
        <p className="text-sm text-red-600">Unable to load call details.</p>
    ) : !selectedCall ? (
        <p className="text-sm text-red-600">Unable to load call details.</p>
    ) : (
        <VoiceCallsDetailPane
            redesigned={uiV3ComponentsEnabled}
            call={selectedCall}
            caregiverName={selectedCallCaregiverName}
            caregiverId={
                selectedCallListItem &&
                selectedCallListItem.kind !== 'notification'
                    ? (selectedCallListItem.caregiver_id ?? null)
                    : null
            }
            isAdmin={Boolean(user?.admin)}
            assignmentEnabled={isAssignmentEnabled}
            onAssignmentChanged={() => refreshCallData(selectedCall.id)}
            onCallerNameChanged={() => refreshCallData(selectedCall.id)}
            isRecordingUrlLoading={isRecordingUrlLoading}
            organizationId={organizationId}
            prefetchedRecordingUrl={recordingUrls?.[selectedCall.id]}
            recordingUrlError={recordingUrlsError}
            silentListeningLabelSettingsEnabled={
                silentListeningLabelSettingsEnabled
            }
            timezone={timezone}
        />
    );

    // v3 Filter menu and active chips share the messages inbox FilterBar.
    const voiceCallFilterDimensions = buildVoiceCallFilterDimensions({
        offices: officeOptions.map((office) => ({
            value: office.id,
            label: office.display_name,
        })),
        lines: lineOptions.map((line) => ({
            value: line.number,
            label:
                line.label?.trim() ||
                formatPhoneNumber(line.number) ||
                line.number,
        })),
        transferDestinations: transferDestinationOptions.map((destination) => ({
            value: destination.id,
            label: destination.name ?? 'Unknown destination',
        })),
        topics: CALL_TOPIC_OPTIONS,
        caregivers: (filterParticipants?.caregivers ?? []).map((caregiver) => ({
            value: caregiver.id,
            label: formatName(caregiver.name),
        })),
        clients: (filterParticipants?.clients ?? []).map((client) => ({
            value: client.id,
            label: formatName(formatClientName(client)),
        })),
        dateFrom: pendingDateFrom,
        dateTo: pendingDateTo,
        onDateFromChange: setPendingDateFrom,
        onDateToChange: setPendingDateTo,
    });
    const voiceCallChips = voiceCallFilterChips({
        office: pendingOfficeIds,
        line: pendingLineNumber === ALL_LINES_VALUE ? [] : [pendingLineNumber],
        call_type:
            pendingCallType === ALL_CALL_TYPES_VALUE ? [] : [pendingCallType],
        transfer:
            pendingTransferStatus === ALL_TRANSFER_STATUSES_VALUE
                ? []
                : [pendingTransferStatus],
        transfer_destination:
            pendingTransferDestinationId === ALL_TRANSFER_DESTINATIONS_VALUE
                ? []
                : [pendingTransferDestinationId],
        voicemail:
            pendingVoicemailStatus === ALL_VOICEMAIL_STATUSES_VALUE
                ? []
                : [pendingVoicemailStatus],
        notes:
            pendingNotesFilter === ALL_NOTES_VALUE ? [] : [pendingNotesFilter],
        topic: pendingTopic === ALL_TOPICS_VALUE ? [] : [pendingTopic],
        caregiver:
            pendingCaregiverId === ALL_CAREGIVERS_VALUE
                ? []
                : [pendingCaregiverId],
        client: pendingClientId === ALL_CLIENTS_VALUE ? [] : [pendingClientId],
        date_from: pendingDateFrom ? [pendingDateFrom] : [],
        date_to: pendingDateTo ? [pendingDateTo] : [],
    });
    const handleVoiceCallChipsChange = (
        chips: Array<{
            readonly dimension: VoiceCallFilterKey;
            readonly values: Array<string>;
        }>,
    ) => {
        const values = voiceCallFilterChipValues(chips);
        const first = (key: VoiceCallFilterKey) => values[key]?.[0];
        setPendingOfficeIds([...(values.office ?? [])]);
        setPendingLineNumber(first('line') ?? ALL_LINES_VALUE);
        const callType = first('call_type');
        setPendingCallType(
            callType === 'client' ||
                callType === 'caregiver' ||
                callType === 'notification'
                ? callType
                : ALL_CALL_TYPES_VALUE,
        );
        const transfer = first('transfer');
        setPendingTransferStatus(
            transfer === 'transferred' || transfer === 'not_transferred'
                ? transfer
                : ALL_TRANSFER_STATUSES_VALUE,
        );
        setPendingTransferDestinationId(
            first('transfer_destination') ?? ALL_TRANSFER_DESTINATIONS_VALUE,
        );
        const voicemail = first('voicemail');
        setPendingVoicemailStatus(
            voicemail === 'voicemail' || voicemail === 'not_voicemail'
                ? voicemail
                : ALL_VOICEMAIL_STATUSES_VALUE,
        );
        setPendingNotesFilter(
            first('notes') === NEEDS_REVIEW_NOTES_VALUE
                ? NEEDS_REVIEW_NOTES_VALUE
                : ALL_NOTES_VALUE,
        );
        const topic = first('topic');
        setPendingTopic(
            topic !== undefined && isCallTopicValue(topic)
                ? topic
                : ALL_TOPICS_VALUE,
        );
        // Caregiver and client filters are mutually exclusive, and neither
        // applies to notification calls, matching the legacy popover rules.
        const caregiverId = first('caregiver');
        const clientId = first('client');
        setPendingCaregiverId(
            callType === 'notification' || callType === 'client' || !caregiverId
                ? ALL_CAREGIVERS_VALUE
                : caregiverId,
        );
        setPendingClientId(
            callType === 'notification' ||
                callType === 'caregiver' ||
                !clientId ||
                (caregiverId !== undefined &&
                    pendingCaregiverId !== caregiverId)
                ? ALL_CLIENTS_VALUE
                : clientId,
        );
        setPendingDateFrom(first('date_from'));
        setPendingDateTo(first('date_to'));
    };
    const officeFilter = (
        <OfficeFilterCombobox
            offices={officeOptions}
            selectedOfficeIds={pendingOfficeIds}
            onSelectedOfficeIdsChange={setPendingOfficeIds}
        />
    );
    const lineFilter =
        lineOptions.length > 1 ? (
            <Select
                value={pendingLineNumber}
                onValueChange={setPendingLineNumber}
            >
                <SelectTrigger
                    aria-label="Line"
                    className="h-9 w-[180px] text-sm"
                >
                    <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL_LINES_VALUE}>All lines</SelectItem>
                    {lineOptions.map((line) => (
                        <SelectItem key={line.number} value={line.number}>
                            {line.label?.trim() ||
                                formatPhoneNumber(line.number)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        ) : null;
    const filtersBlock = (
        <Fragment>
            {uiV3ComponentsEnabled && (
                <SearchInput
                    containerClassName="w-full min-w-0"
                    onValueChange={setPendingSearch}
                    placeholder={
                        pendingCallType === 'notification'
                            ? 'Search announcements or numbers…'
                            : 'Search calls…'
                    }
                    value={pendingSearch}
                />
            )}
            {savedViewStorageKey && !uiV3ComponentsEnabled ? (
                <LocalSavedViewsBar
                    views={savedViews.views}
                    currentPayload={currentSavedViewPayload}
                    activeViewId={activeSavedViewId}
                    matchesPayload={voiceCallSavedViewPayloadsEqual}
                    onApplyView={(view) => applySavedView(view.payload)}
                    onActiveViewChange={setActiveSavedViewId}
                    onCreateView={savedViews.createView}
                    onUpdateView={savedViews.updateView}
                    onRenameView={savedViews.renameView}
                    onDeleteView={savedViews.deleteView}
                    navigationLabel="Voice call saved views"
                />
            ) : null}
            <div
                className={cn(
                    uiV3ComponentsEnabled
                        ? // Same one-line pill row as the messages inbox,
                          // including its negative margins so the pills sit
                          // flush with the search box.
                          'group/fit -mx-2 -my-1 flex items-center gap-1 px-2 py-1'
                        : 'flex flex-wrap items-end gap-3',
                )}
                data-testid="voice-calls-filters"
                role={uiV3ComponentsEnabled ? 'toolbar' : undefined}
                aria-label={uiV3ComponentsEnabled ? 'Call filters' : undefined}
            >
                {uiV3ComponentsEnabled ? (
                    <Fragment>
                        <InboxFilterPill
                            active={pendingDirection === 'inbound'}
                            label="Inbound"
                            onClick={() =>
                                setPendingDirection((previous) =>
                                    previous === 'inbound'
                                        ? ALL_DIRECTIONS_VALUE
                                        : 'inbound',
                                )
                            }
                        />
                        <InboxFilterPill
                            active={pendingDirection === 'outbound'}
                            label="Outbound"
                            onClick={() =>
                                setPendingDirection((previous) =>
                                    previous === 'outbound'
                                        ? ALL_DIRECTIONS_VALUE
                                        : 'outbound',
                                )
                            }
                        />
                    </Fragment>
                ) : (
                    <div className="min-w-[320px] flex-[1_1_440px] md:max-w-[560px]">
                        <SearchInput
                            value={pendingSearch}
                            onValueChange={setPendingSearch}
                            placeholder={
                                pendingCallType === 'notification'
                                    ? 'Search announcements or full phone numbers…'
                                    : 'Search by name, number, or keyword…'
                            }
                            className="h-9 text-sm"
                        />
                    </div>
                )}
                {!uiV3ComponentsEnabled && dateFilters}
                {!uiV3ComponentsEnabled && (
                    <Select
                        value={pendingDirection}
                        onValueChange={(value) => {
                            if (
                                value === ALL_DIRECTIONS_VALUE ||
                                value === 'inbound' ||
                                value === 'outbound'
                            ) {
                                setPendingDirection(value);
                            }
                        }}
                    >
                        <SelectTrigger
                            aria-label="Direction"
                            className="h-9 w-[140px] text-sm"
                        >
                            <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_DIRECTIONS_VALUE}>
                                All
                            </SelectItem>
                            <SelectItem value="inbound">Inbound</SelectItem>
                            <SelectItem value="outbound">Outbound</SelectItem>
                        </SelectContent>
                    </Select>
                )}
                {!uiV3ComponentsEnabled && officeFilter}
                {!uiV3ComponentsEnabled && lineFilter}
                {isAssignmentEnabled && isTableView ? (
                    <div className="flex gap-1">
                        <Button
                            type="button"
                            variant="outline"
                            className={cn(
                                'h-9',
                                pendingAssignment === 'assigned' &&
                                    'border-primary text-primary',
                            )}
                            onClick={() =>
                                setPendingAssignment((previous) =>
                                    previous === 'assigned'
                                        ? ALL_ASSIGNMENTS_VALUE
                                        : 'assigned',
                                )
                            }
                        >
                            Assigned
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className={cn(
                                'h-9',
                                pendingAssignment === 'unassigned' &&
                                    'border-primary text-primary',
                            )}
                            onClick={() =>
                                setPendingAssignment((previous) =>
                                    previous === 'unassigned'
                                        ? ALL_ASSIGNMENTS_VALUE
                                        : 'unassigned',
                                )
                            }
                        >
                            Unassigned
                        </Button>
                    </div>
                ) : null}
                {uiV3ComponentsEnabled ? (
                    <FilterBar
                        chips={voiceCallChips}
                        dimensions={voiceCallFilterDimensions}
                        layout="trigger"
                        onChange={handleVoiceCallChipsChange}
                        triggerClassName={INBOX_PILL_COMPACT_CLASS}
                        triggerVariant="labeled"
                    />
                ) : (
                    <Popover>
                        <PopoverTrigger asChild={true}>
                            <Button
                                type="button"
                                variant="outline"
                                className={cn(
                                    'h-9',
                                    secondaryFilterCount > 0 &&
                                        'border-primary text-primary',
                                )}
                            >
                                <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                                Filters
                                {secondaryFilterCount > 0 && (
                                    <Badge className="ml-1.5 h-5 min-w-5 justify-center rounded-full px-1">
                                        {secondaryFilterCount}
                                    </Badge>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="end"
                            className="w-[calc(100vw-2rem)] max-w-[460px] p-4 sm:w-[460px] sm:p-5"
                        >
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="flex flex-col gap-1">
                                    <span className="text-muted-foreground text-xs font-medium">
                                        Call type
                                    </span>
                                    <Select
                                        value={pendingCallType}
                                        onValueChange={(value) => {
                                            if (
                                                value ===
                                                    ALL_CALL_TYPES_VALUE ||
                                                value === 'client' ||
                                                value === 'caregiver' ||
                                                value === 'notification'
                                            ) {
                                                setPendingCallType(value);
                                                if (value === 'notification') {
                                                    setPendingCaregiverId(
                                                        ALL_CAREGIVERS_VALUE,
                                                    );
                                                    setPendingClientId(
                                                        ALL_CLIENTS_VALUE,
                                                    );
                                                }
                                                if (value === 'client') {
                                                    setPendingCaregiverId(
                                                        ALL_CAREGIVERS_VALUE,
                                                    );
                                                } else if (
                                                    value === 'caregiver'
                                                ) {
                                                    setPendingClientId(
                                                        ALL_CLIENTS_VALUE,
                                                    );
                                                }
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="h-9 w-full text-sm">
                                            <SelectValue placeholder="All" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                value={ALL_CALL_TYPES_VALUE}
                                            >
                                                All except notifications
                                            </SelectItem>
                                            <SelectItem value="client">
                                                Client
                                            </SelectItem>
                                            <SelectItem value="caregiver">
                                                Caregiver
                                            </SelectItem>
                                            <SelectItem value="notification">
                                                Notification calls
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-muted-foreground text-xs font-medium">
                                        Transfer
                                    </span>
                                    <Select
                                        value={pendingTransferStatus}
                                        onValueChange={(value) => {
                                            if (
                                                value ===
                                                    ALL_TRANSFER_STATUSES_VALUE ||
                                                value === 'transferred' ||
                                                value === 'not_transferred'
                                            ) {
                                                setPendingTransferStatus(value);
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="h-9 w-full text-sm">
                                            <SelectValue placeholder="All" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                value={
                                                    ALL_TRANSFER_STATUSES_VALUE
                                                }
                                            >
                                                All
                                            </SelectItem>
                                            <SelectItem value="transferred">
                                                Transferred
                                            </SelectItem>
                                            <SelectItem value="not_transferred">
                                                Not transferred
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {transferDestinationOptions.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-muted-foreground text-xs font-medium">
                                            Transferred to
                                        </span>
                                        <Select
                                            value={pendingTransferDestinationId}
                                            onValueChange={
                                                setPendingTransferDestinationId
                                            }
                                        >
                                            <SelectTrigger
                                                aria-label="Transferred to"
                                                className="h-9 w-full text-sm"
                                            >
                                                <SelectValue placeholder="All" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem
                                                    value={
                                                        ALL_TRANSFER_DESTINATIONS_VALUE
                                                    }
                                                >
                                                    All
                                                </SelectItem>
                                                {transferDestinationOptions.map(
                                                    (destination) => (
                                                        <SelectItem
                                                            key={destination.id}
                                                            value={
                                                                destination.id
                                                            }
                                                        >
                                                            {destination.name}
                                                        </SelectItem>
                                                    ),
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div className="flex flex-col gap-1">
                                    <span className="text-muted-foreground text-xs font-medium">
                                        Voicemail
                                    </span>
                                    <Select
                                        value={pendingVoicemailStatus}
                                        onValueChange={(value) => {
                                            if (
                                                value ===
                                                    ALL_VOICEMAIL_STATUSES_VALUE ||
                                                value === 'voicemail' ||
                                                value === 'not_voicemail'
                                            ) {
                                                setPendingVoicemailStatus(
                                                    value,
                                                );
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="h-9 w-full text-sm">
                                            <SelectValue placeholder="All" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                value={
                                                    ALL_VOICEMAIL_STATUSES_VALUE
                                                }
                                            >
                                                All
                                            </SelectItem>
                                            <SelectItem value="voicemail">
                                                Voicemail
                                            </SelectItem>
                                            <SelectItem value="not_voicemail">
                                                No voicemail
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-muted-foreground text-xs font-medium">
                                        Notes
                                    </span>
                                    <Select
                                        value={pendingNotesFilter}
                                        onValueChange={(value) => {
                                            if (
                                                value === ALL_NOTES_VALUE ||
                                                value ===
                                                    NEEDS_REVIEW_NOTES_VALUE
                                            ) {
                                                setPendingNotesFilter(value);
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="h-9 w-full text-sm">
                                            <SelectValue placeholder="All" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL_NOTES_VALUE}>
                                                All
                                            </SelectItem>
                                            <SelectItem
                                                value={NEEDS_REVIEW_NOTES_VALUE}
                                            >
                                                Needs review
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-muted-foreground text-xs font-medium">
                                        Topic
                                    </span>
                                    <Select
                                        value={pendingTopic}
                                        onValueChange={(value) => {
                                            if (
                                                value === ALL_TOPICS_VALUE ||
                                                isCallTopicValue(value)
                                            ) {
                                                setPendingTopic(value);
                                            }
                                        }}
                                    >
                                        <SelectTrigger
                                            aria-label="Topic"
                                            className="h-9 w-full text-sm"
                                        >
                                            <SelectValue placeholder="All" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                value={ALL_TOPICS_VALUE}
                                            >
                                                All
                                            </SelectItem>
                                            {CALL_TOPIC_OPTIONS.map(
                                                (option) => (
                                                    <SelectItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {pendingCallType !== 'client' && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-muted-foreground text-xs font-medium">
                                            Caregiver
                                        </span>
                                        <CaregiverFilterCombobox
                                            organizationId={organizationId}
                                            value={pendingCaregiverId}
                                            onValueChange={(value) => {
                                                setPendingCaregiverId(value);
                                                if (
                                                    value !==
                                                    ALL_CAREGIVERS_VALUE
                                                ) {
                                                    setPendingClientId(
                                                        ALL_CLIENTS_VALUE,
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                                {pendingCallType !== 'caregiver' && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-muted-foreground text-xs font-medium">
                                            Client
                                        </span>
                                        <ClientFilterCombobox
                                            organizationId={organizationId}
                                            value={pendingClientId}
                                            onValueChange={(value) => {
                                                setPendingClientId(value);
                                                if (
                                                    value !== ALL_CLIENTS_VALUE
                                                ) {
                                                    setPendingCaregiverId(
                                                        ALL_CAREGIVERS_VALUE,
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
                {hasActiveFilters && !uiV3ComponentsEnabled && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-9"
                        onClick={handleClearFilters}
                    >
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        Clear
                    </Button>
                )}
            </div>
            {uiV3ComponentsEnabled ? (
                <FilterBar
                    chips={voiceCallChips}
                    dimensions={voiceCallFilterDimensions}
                    layout="active-row"
                    onChange={handleVoiceCallChipsChange}
                    onClear={handleClearFilters}
                    showClear={voiceCallChips.length > 0}
                />
            ) : (
                secondaryFilterChips.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                        {secondaryFilterChips.map((chip) => (
                            <span
                                key={chip.key}
                                className="bg-secondary text-secondary-foreground inline-flex items-center gap-1.5 rounded-full py-1 pr-1 pl-2.5 text-xs font-medium"
                            >
                                <span className="text-muted-foreground">
                                    {chip.label}:
                                </span>
                                {chip.value}
                                <button
                                    type="button"
                                    onClick={chip.onRemove}
                                    aria-label={`Remove ${chip.label} filter`}
                                    className="hover:bg-foreground/10 ml-0.5 rounded-full p-0.5"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )
            )}
        </Fragment>
    );

    const listBlock = (
        <div
            className="min-h-0 flex-1 overflow-y-auto"
            onScroll={handleCallListScroll}
            data-testid="voice-call-list"
        >
            {isPageLoading && pageCalls.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                </div>
            ) : callsError && pageCalls.length === 0 ? (
                <p className="p-4 text-sm text-red-600">
                    Unable to load voice calls.
                </p>
            ) : pageCalls.length === 0 ? (
                <VoiceCallsEmptyState
                    hasActiveFilters={hasActiveFilters}
                    canSearchOlderCalls={canSearchOlderCalls}
                    isLoadingMore={isLoadingMoreCalls}
                    onSearchOlderCalls={loadMoreCalls}
                />
            ) : (
                <Fragment>
                    <CallListRows
                        redesigned={uiV3ComponentsEnabled}
                        calls={pageCalls}
                        onSelect={handleSelectCall}
                        selectedCallId={isMobile ? null : selectedCallId}
                        silentListeningLabelSettingsEnabled={
                            silentListeningLabelSettingsEnabled
                        }
                        timezone={timezone}
                    />
                    {isLoadingMoreCalls && (
                        <div className="flex items-center justify-center py-4">
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        </div>
                    )}
                    {canSearchOlderCalls && (
                        <div className="flex justify-center py-4">
                            <SearchOlderVoiceCallsButton
                                isLoading={isLoadingMoreCalls}
                                onSearch={loadMoreCalls}
                            />
                        </div>
                    )}
                    {!hasMoreCalls && !isLoadingMoreCalls && (
                        <p className="text-muted-foreground py-4 text-center text-xs">
                            End of voice calls
                        </p>
                    )}
                </Fragment>
            )}
        </div>
    );

    if (uiV3ComponentsEnabled && !isTableView) {
        // Mirrors the conversations inbox: the list column on the left with the
        // page tabs, search, and filters in its header; the selected call opens
        // in the pane where a conversation thread would. The opt-in dense
        // table keeps the legacy layout below so `view=table` stays reachable.
        return (
            <TrainingModePageTourScope
                pageId="voice-calls"
                organizationId={organizationId}
                ready={!isPageLoading}
                steps={VOICE_CALLS_PAGE_TOUR_STEPS}
            >
                <div className="bg-background relative flex h-full min-h-0 w-full overflow-hidden">
                    <div className="@container/inbox relative flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden border-r md:w-[max(24rem,27vw)] md:max-w-[36rem]">
                        <div className="shrink-0 space-y-3 px-3 pt-4 pb-3">
                            <div className="px-1">
                                <ConversationsPageTabs
                                    active="calls"
                                    actions={headerActions}
                                />
                            </div>
                            {filtersBlock}
                        </div>
                        {listBlock}
                    </div>
                    <div
                        className="hidden min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex"
                        data-testid={
                            isMobile ? undefined : 'voice-calls-detail-pane'
                        }
                    >
                        {selectedCallId ? (
                            // The detail pane owns its header, scrolling, and
                            // side panel so the caller name lands where a
                            // conversation title does; only the loading and
                            // error states need an inset here.
                            <div
                                className={cn(
                                    'flex min-h-0 flex-1 flex-col',
                                    !selectedCall && 'p-4',
                                )}
                            >
                                {selectedCallDetailContent}
                            </div>
                        ) : (
                            <EmptyState
                                icon={Phone}
                                title="No call selected"
                                description="Pick a call on the left."
                                className="mt-12"
                            />
                        )}
                    </div>
                    {isMobile && (
                        <CallDetailDialog
                            organizationId={organizationId}
                            voiceCallId={mobileDialogCallId}
                            prefetchedCall={mobileDialogCall}
                            caregiverName={mobileDialogCallerName}
                            open={mobileDialogCallId !== null}
                            onOpenChange={(open) => {
                                if (!open) {
                                    selectVoiceCall(null, { replace: true });
                                }
                            }}
                            timezone={timezone}
                        />
                    )}
                </div>
            </TrainingModePageTourScope>
        );
    }

    return (
        <TrainingModePageTourScope
            pageId="voice-calls"
            organizationId={organizationId}
            ready={!isPageLoading}
            steps={VOICE_CALLS_PAGE_TOUR_STEPS}
        >
            <div
                className={cn(
                    'flex h-full min-h-0 w-full flex-col',
                    APP_PAGE_GUTTER_CLASSNAME,
                    !isTableView && 'mx-auto max-w-[90rem]',
                )}
            >
                <div className="mb-4 border-b pb-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="text-muted-foreground hover:text-foreground hidden items-center text-sm md:inline-flex"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                    </button>
                    <div className="mt-0 mb-2 flex items-center justify-between gap-3 md:mt-4">
                        <h1 className="flex items-center gap-2 text-xl font-semibold">
                            <Phone className="text-foreground h-5 w-5" />
                            Voice Calls
                        </h1>
                        <div className="flex items-center gap-2">
                            {headerActions}
                        </div>
                    </div>
                    <div className="mt-3 space-y-3">{filtersBlock}</div>
                </div>
                {isTableView ? (
                    <div
                        className="min-h-0 flex-1 overflow-auto rounded-lg border"
                        data-testid="voice-call-table"
                    >
                        {isPageLoading && pageCalls.length === 0 ? (
                            <div className="flex items-center justify-center py-10">
                                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                            </div>
                        ) : callsError && pageCalls.length === 0 ? (
                            <p className="p-4 text-sm text-red-600">
                                Unable to load voice calls.
                            </p>
                        ) : pageCalls.length === 0 ? (
                            <VoiceCallsEmptyState
                                hasActiveFilters={hasActiveFilters}
                                canSearchOlderCalls={canSearchOlderCalls}
                                isLoadingMore={isLoadingMoreCalls}
                                onSearchOlderCalls={loadMoreCalls}
                            />
                        ) : (
                            <Fragment>
                                <VoiceCallsTable
                                    calls={pageCalls}
                                    timezone={timezone}
                                    selectedVoiceCallId={
                                        voiceCallIdFromSearch ?? null
                                    }
                                    onSelectCall={handleSelectTableCall}
                                    isLoadingMore={isLoadingMoreCalls}
                                    hasMore={
                                        hasMoreCalls && !canSearchOlderCalls
                                    }
                                    onLoadMore={loadMoreCalls}
                                    assignmentEnabled={isAssignmentEnabled}
                                    organizationId={organizationId}
                                    onAssignmentChanged={refreshCallData}
                                    silentListeningLabelSettingsEnabled={
                                        silentListeningLabelSettingsEnabled
                                    }
                                />
                                {canSearchOlderCalls && (
                                    <div className="flex justify-center p-4">
                                        <SearchOlderVoiceCallsButton
                                            isLoading={isLoadingMoreCalls}
                                            onSearch={loadMoreCalls}
                                        />
                                    </div>
                                )}
                            </Fragment>
                        )}
                    </div>
                ) : (
                    <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-lg border md:grid-cols-[360px_minmax(0,1fr)]">
                        <div className="flex min-h-0 flex-col md:border-r">
                            {listBlock}
                        </div>
                        <div
                            className="hidden min-h-0 min-w-0 overflow-x-hidden overflow-y-auto md:block"
                            // The pane is display:none on mobile, so only
                            // expose the tour anchor when it is visible.
                            data-testid={
                                isMobile ? undefined : 'voice-calls-detail-pane'
                            }
                        >
                            <div className="space-y-4 p-4 md:space-y-6 md:p-6">
                                {selectedCallDetailContent}
                            </div>
                        </div>
                    </div>
                )}
                {(isMobile || isTableView) && (
                    <CallDetailDialog
                        organizationId={organizationId}
                        voiceCallId={mobileDialogCallId}
                        prefetchedCall={mobileDialogCall}
                        caregiverName={mobileDialogCallerName}
                        open={mobileDialogCallId !== null}
                        onOpenChange={(open) => {
                            if (!open) {
                                // Clear the deep-link param so the URL stays in
                                // sync; the search-param effect then closes the
                                // dialog. Without this, the stale voiceCallId
                                // would block re-tapping the same call.
                                selectVoiceCall(null, { replace: true });
                            }
                        }}
                        timezone={timezone}
                    />
                )}
            </div>
        </TrainingModePageTourScope>
    );
}
